import type { StorageAdapter } from '@/services/storage/storage-adapter';
import type { LogEntry, CreateLogEntryInput, UpdateLogEntryInput } from '@/types/log-entry';
import { recordPendingDelete } from './sync-service';
import { posthogService } from '@/services/posthog-service';
import { EVENT_LOG_ENTRY_CREATED } from '@/services/observability/events';

export class LogEntryService {
  constructor(private adapter: StorageAdapter) {}

  async getEntries(waypointId: string): Promise<LogEntry[]> {
    return this.adapter.getLogEntries(waypointId);
  }

  async getEntriesByProperty(propertyId: string): Promise<LogEntry[]> {
    return this.adapter.getLogEntriesByProperty(propertyId);
  }

  async getEntriesByDateRange(waypointId: string, start: Date, end: Date): Promise<LogEntry[]> {
    return this.adapter.getLogEntriesByDateRange(waypointId, start, end);
  }

  async getRecentEntries(limit?: number): Promise<LogEntry[]> {
    return this.adapter.getRecentLogEntries(limit);
  }

  /**
   * Returns all log entries whose date falls within [start, end] (inclusive, YYYY-MM-DD strings).
   * Loads from the full recent entries list (no per-waypointId restriction).
   * Suitable for season recap where entries span all user properties.
   */
  async getLogEntriesInDateRange(start: string, end: string): Promise<LogEntry[]> {
    // Use a generous limit — typical season spans ~3 months with moderate scouting activity
    const all = await this.adapter.getRecentLogEntries(2000);
    return all.filter((e) => e.date >= start && e.date <= end);
  }

  async createEntry(data: CreateLogEntryInput): Promise<LogEntry> {
    const entry = await this.adapter.createLogEntry(data);
    posthogService.track(EVENT_LOG_ENTRY_CREATED, {
      has_photo: (entry.photos?.length ?? 0) > 0,
      has_observation: entry.animalObserved ?? false,
    });
    return entry;
  }

  async updateEntry(id: string, data: UpdateLogEntryInput): Promise<LogEntry> {
    return this.adapter.updateLogEntry(id, data);
  }

  async deleteEntry(id: string): Promise<void> {
    await this.adapter.deleteLogEntry(id);
    await recordPendingDelete(id, 'logEntry');
  }
}
